import React, { useState, Fragment } from 'react';
import { Check, ChevronDown, Sparkles, Calendar, Mail } from 'lucide-react';
import { PageHeader } from '../components/page/PageHeader';
import { Reveal } from '../components/ui/Reveal';
import { Button } from '../components/ui/Button';
import { MarketplaceModal } from '../components/MarketplaceModal';
import { HowMarketplaceWorks } from '../components/page/HowMarketplaceWorks';
interface Tier {
  name: string;
  desc: string;
  monthly: string;
  annual: string;
  note: string;
  cta: string;
  features: string[];
  highlight: boolean;
}
const tiers: Tier[] = [{
  name: 'Casting',
  desc: 'Post casting calls and receive custom submissions',
  monthly: '$149',
  annual: '$125',
  note: '40% off Beta pricing',
  cta: 'Try Casting',
  features: ['30-day free trial', 'Post unlimited casting calls', 'Access a network of 400+ talent agencies', 'Receive talent submissions & quotes from talent agencies', 'Live influencer metrics & audience demographic data', 'Direct talent manager contact details', 'Search & filter submissions', '3 users'],
  highlight: false
}, {
  name: 'Marketplace',
  desc: 'Search and discover represented talent',
  monthly: '$239',
  annual: '$199',
  note: '40% off Beta pricing',
  cta: 'Request Marketplace Access',
  features: ['Approval required', '30-day free trial', 'Everything in Casting', 'Search 6,000 represented influencers', 'Browse 400+ talent agency rosters', 'Marketplace insights & pricing benchmarks', 'Consulting sessions', 'Unlimited users', 'Priority customer support'],
  highlight: true
}];
const faqs = [{
  q: 'How does the free trial work?',
  a: 'Both Casting and Marketplace plans come with a 30-day free trial so you can explore the platform and post your first casting call risk-free.'
}, {
  q: 'Why does Marketplace require approval?',
  a: 'The roster database and talent agency directory contain sensitive contact and pricing information. We vet users to ensure a high-quality environment for our talent agencies.'
}, {
  q: 'Can I switch plans later?',
  a: 'Anytime. Plans are monthly or annual, and you can upgrade or downgrade whenever your needs change.'
}, {
  q: 'Who owns the creator data?',
  a: 'Talent agencies own and manage their creators’ profiles. Brands and agencies always see live, agency-maintained data that stays current.'
}];
const stats = ['400+ Talent Agencies', '6,000+ Represented Influencers', '$500K+ GMV Facilitated'];
const FaqItem = ({
  q,
  a
}: {
  q: string;
  a: string;
}) => {
  const [open, setOpen] = useState(false);
  return <div className="border-b border-gray-200 py-5" data-id="element-2384" data-mp="1y9kzio56989n">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left gap-4" data-id="element-2385" data-mp="1t96u7duygcfb">
        <span className="font-semibold text-gray-900" data-id="element-2386" data-mp="1oa2fy8t6boi4">{q}</span>
        <ChevronDown size={20} className={`text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-2387" data-mp="3sb7pcywvmky" />
      </button>
      {open && <p className="mt-3 text-gray-600 leading-relaxed" data-id="element-2388" data-mp="dppm7719y1am">{a}</p>}
    </div>;
};
export const Pricing = () => {
  const [annual, setAnnual] = useState(false);
  const [isMarketplaceModalOpen, setIsMarketplaceModalOpen] = useState(false);
  return <>
      <MarketplaceModal isOpen={isMarketplaceModalOpen} onClose={() => setIsMarketplaceModalOpen(false)} data-id="element-2389" data-mp="3h7pqab3plmiy" />
      <PageHeader eyebrow="For Brands & Marketing Agencies" title="Get direct access to hundreds of talent agencies" subtitle="Discover talent, post casting calls and receive curated submissions directly from influencer talent agencies." data-id="element-2390" data-mp="3m3en46ocgpbk" />

      {/* Stats + beta banner */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 mb-10 text-center" data-id="element-2391" data-mp="2ye3w3gwsq9am">
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm font-medium text-gray-500" data-id="element-2392" data-mp="32ffd4y1ltm77">
          {stats.map((s, i) => <Fragment key={s} data-id="element-2393" data-mp="2eev9ou7rauzw" data-mpi={i}>
              <span data-id="element-2394" data-mp="2efn6u9h050sa">{s}</span>
              {i < stats.length - 1 && <span className="text-gray-300" aria-hidden="true" data-id="element-2395" data-mp="2t3rorb88d8a0">
                  |
                </span>}
            </Fragment>)}
        </div>
        <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-zano-lavender/40 border border-zano-pink/20 px-4 py-2 text-sm" data-id="element-2396" data-mp="322vayo2p23sq">
          <Sparkles size={16} className="text-zano-pink" data-id="element-2397" data-mp="2x3s4cz72fpin" />
          <span className="text-gray-700" data-id="element-2398" data-mp="3c2427966d4be">
            Beta Pricing —{' '}
            <span className="font-semibold text-zano-pink" data-id="element-2399" data-mp="372hokymfpm22">
              Lock in 40% off
            </span>{' '}
            for life
          </span>
        </div>
      </div>

      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-3 mb-12" data-id="element-2400" data-mp="1coggro8wpopu">
        <span className={`text-sm font-semibold ${annual ? 'text-zano-charcoal' : 'text-gray-400'}`} data-id="element-2401" data-mp="1cqazqx0mb6ra">
          Annual{' '}
          <span className={annual ? 'text-zano-pink' : 'text-gray-400'} data-id="element-2402" data-mp="3tbeoqz5hx10l">
            (save 20%)
          </span>
        </span>
        <button onClick={() => setAnnual(!annual)} aria-label="Toggle annual billing" className="relative w-12 h-6 rounded-full bg-zano-pink/20 transition-colors" data-id="element-2403" data-mp="3el7f98xgusc1">
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-zano-pink shadow transition-transform ${!annual ? 'translate-x-6' : ''}`} data-id="element-2404" data-mp="35kj1mfbzitzo" />
        </button>
        <span className={`text-sm font-semibold ${!annual ? 'text-zano-charcoal' : 'text-gray-400'}`} data-id="element-2405" data-mp="2vo6579p22218">
          Monthly
        </span>
      </div>

      {/* Tiers */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-2406" data-mp="tvi2d1gbpglz">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch" data-id="element-2407" data-mp="yum9znnln6a7">
          {tiers.map((t, i) => <Reveal key={t.name} delay={i * 0.08} className="h-full" data-id="element-2408" data-mp="xe8iqxb7a379" data-mpi={i}>
              <div className={`relative h-full flex flex-col rounded-2xl border p-7 ${t.highlight ? 'border-zano-pink shadow-float bg-white' : 'border-gray-200 bg-white shadow-sm'}`} data-id="element-2409" data-mp="xbcl7a39lgjc">
                {t.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-zano-pink text-white text-xs font-semibold px-3 py-1 rounded-full" data-id="element-2410" data-mp="ihq4123w513e">
                    Most Popular
                  </span>}
                <h3 className="text-lg font-bold text-gray-900" data-id="element-2411" data-mp="1b5vnzmn4em26">{t.name}</h3>
                <p className={`text-sm text-gray-500 mb-5 ${t.name === 'Casting' ? 'md:whitespace-nowrap' : ''}`} data-id="element-2412" data-mp="16d2kt37avls7">
                  {t.desc}
                </p>
                <div className="mb-1 flex items-end gap-1" data-id="element-2413" data-mp="1yvgagfihg1g7">
                  <span className="text-4xl font-bold text-zano-charcoal" data-id="element-2414" data-mp="1u480nid8qe2i">
                    {annual ? t.annual : t.monthly}
                  </span>
                  <span className="text-sm text-gray-500 mb-1" data-id="element-2415" data-mp="22e6po81mihzw">
                    {annual ? '/ month, billed annually' : '/ month'}
                  </span>
                </div>
                <p className="text-sm font-semibold text-zano-pink mb-6" data-id="element-2416" data-mp="2bc8uyzse3wsz">
                  {t.note}
                </p>
                {t.name === 'Marketplace' ? <Button variant={t.highlight ? 'primary' : 'secondary'} className="w-full justify-center" onClick={() => setIsMarketplaceModalOpen(true)} data-id="element-2417" data-mp="2lbpybak65yls">
                    {t.cta}
                  </Button> : <a href="https://app.zanoplatform.com/get-started?tier=explorer&type=brand&cycle=monthly" className="block" data-id="element-2418" data-mp="2z34ktrdachxl">
                    <Button variant={t.highlight ? 'primary' : 'secondary'} className="w-full justify-center" data-id="element-2419" data-mp="390sppa4lv5th">
                      {t.cta}
                    </Button>
                  </a>}
                <ul className="mt-7 space-y-3" data-id="element-2420" data-mp="3pf8x5nlckznw">
                  {t.features.map(f => <li key={f} className="flex items-start gap-2.5 text-sm text-gray-700" data-id="element-2421" data-mp="75707jf9flp1">
                      <Check size={16} className="text-zano-pink mt-0.5 shrink-0" data-id="element-2422" data-mp="3ap1ofzl56v0e" />{' '}
                      {f}
                    </li>)}
                </ul>
              </div>
            </Reveal>)}
        </div>
      </section>

      <HowMarketplaceWorks audience="brands" background="white" data-id="element-2423" data-mp="3fls4wo0uzsmc" />

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-2424" data-mp="2rvxro5cmo21k">
        <Reveal data-id="element-2425" data-mp="2mz7atft012f3">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8" data-id="element-2426" data-mp="2mxnx9wyq95h0">
            Frequently asked questions
          </h2>
          <div data-id="element-2427" data-mp="1773gno5xgyw6">
            {faqs.map(f => <FaqItem key={f.q} q={f.q} a={f.a} data-id="element-2428" data-mp="177wsh3jquel6" />)}
          </div>
        </Reveal>
      </section>

      {/* Bottom CTA */}
      <section className="py-20" data-id="element-2429" data-mp="tdvp1whuk3eh">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2430" data-mp="ye9ztmau1rg7">
          <Reveal data-id="element-2431" data-mp="ycfc6086fl8f">
            <div className="bg-zano-charcoal rounded-3xl p-10 md:p-16 text-center text-white relative overflow-hidden" data-id="element-2432" data-mp="1m4niwnbfwfyn">
              <div className="absolute top-0 right-0 w-64 h-64 bg-zano-pink/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-2433" data-mp="1q692ssme5uic" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-zano-lavender/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-2434" data-mp="1c5ybjoh66dwn" />
              <div className="relative z-10 max-w-2xl mx-auto" data-id="element-2435" data-mp="rb1l74hsbg7">
                <h2 className="text-3xl md:text-4xl font-bold mb-5" data-id="element-2436" data-mp="t48mnj0oxoq">
                  Not sure which plan fits?
                </h2>
                <p className="text-lg text-gray-300 mb-8" data-id="element-2437" data-mp="k7zz46tfju2o">
                  Book a demo with us, or ask us anything by emailing{' '}
                  <a href="mailto:support@zanoplatform.com" className="text-white font-semibold underline underline-offset-2 hover:text-zano-pink transition-colors" data-id="element-2438" data-mp="p5jhnix5ut0f">
                    support@zanoplatform.com
                  </a>
                  .
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2439" data-mp="1jd0a61uk0xj">
                  <a href="mailto:support@zanoplatform.com?subject=Book%20a%20demo%20with%20Zano" className="w-full sm:w-auto" data-id="element-2440" data-mp="5gr4fjao7iio">
                    <Button variant="primary" className="bg-white text-zano-charcoal hover:bg-gray-100 w-full sm:w-auto px-8 py-3" data-id="element-2441" data-mp="3sntkayyphtq2">
                      <Calendar size={18} className="mr-1.5" data-id="element-2442" data-mp="1qvf4q6jmx1ct" />
                      Book a Demo
                    </Button>
                  </a>
                  <a href="mailto:support@zanoplatform.com" className="w-full sm:w-auto" data-id="element-2443" data-mp="36fe9rc7sxuqh">
                    <Button variant="secondary" className="bg-transparent text-white border-gray-600 hover:bg-gray-800 hover:border-gray-500 w-full sm:w-auto px-8 py-3" data-id="element-2444" data-mp="3l5lobguoutlw">
                      <Mail size={18} className="mr-1.5" data-id="element-2445" data-mp="3p5noflkrzez2" />
                      Email Support
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>;
};